import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Modal,
    Form,
    Select,
    Tag,
    Space,
    Tabs,
    message,
    Spin,
    Typography,
    Row,
    Col,
    Divider,
    List,
    Badge,
    Timeline,
    Alert,
    Statistic,
    Progress,
    Drawer
} from 'antd';
import {
    CrownOutlined,
    CheckOutlined,
    StarOutlined,
    CalendarOutlined,
    HistoryOutlined,
    RocketOutlined,
    GiftOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    PlusOutlined,
    FireOutlined,
    TrophyOutlined,
    ThunderboltOutlined,
    MenuOutlined,
    WarningOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
} from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';
import { BiDiamond } from 'react-icons/bi';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

export const UserSubscription = () => {
    const [plans, setPlans] = useState([]);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [plansLoading, setPlansLoading] = useState(false);
    const [currentPlanLoading, setCurrentPlanLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const [billingType, setBillingType] = useState('monthly');
    const [isMobile, setIsMobile] = useState(false);

    // Modal states
    const [subscribeVisible, setSubscribeVisible] = useState(false);
    const [planChangeWarningVisible, setPlanChangeWarningVisible] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [changeType, setChangeType] = useState(null); // 'upgrade', 'downgrade', or null

    // Form instances
    const [subscribeForm] = Form.useForm();

    // Check if mobile
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    // Separate API calls
    const fetchPlans = async () => {
        setPlansLoading(true);
        try {
            const plansRes = await drEasyApi.get('/api/Plans/GetAllPlans');
            console.log('Plans Response:', plansRes);
            setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
        } catch (error) {
            console.error('Error fetching plans:', error);
            setPlans([]);
        }
        setPlansLoading(false);
    };

    const fetchCurrentPlan = async () => {
        setCurrentPlanLoading(true);
        try {
            const myPlan = await drEasyApi.get('/api/Plans/MyPlan');
            console.log('Current Plan Response:', myPlan);
            setCurrentPlan(myPlan.data);
        } catch (error) {
            console.error('Error fetching current plan:', error);
            setCurrentPlan(null);
        }
        setCurrentPlanLoading(false);
    };

    const fetchSubscriptionHistory = async () => {
        setHistoryLoading(true);
        try {
            const subHistory = await drEasyApi.get('/api/Plans/MySubscriptionHistory');
            console.log('Subscription History Response:', subHistory);
            
            const transformedHistory = Array.isArray(subHistory.data) 
                ? subHistory.data.map((item, index) => ({
                    id: index + 1,
                    planName: item.planName,
                    price: item.amountPaid,
                    duration: item.duration,
                    subscriptionDate: item.startDate,
                    endDate: item.endDate,
                    status: item.status,
                    isFreeTrial: item.isFreeTrial
                }))
                : [];
            
            setSubscriptionHistory(transformedHistory);
        } catch (error) {
            console.error('Error fetching subscription history:', error);
            setSubscriptionHistory([]);
        }
        setHistoryLoading(false);
    };

    const fetchAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchPlans(),
                fetchCurrentPlan(),
                fetchSubscriptionHistory()
            ]);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    // Helper functions
    const isUserOnActivePlan = () => {
        return currentPlan && currentPlan.status === "Active";
    };

    const getDaysRemaining = () => {
        if (currentPlan && currentPlan.endDate) {
            const today = new Date();
            const expiry = new Date(currentPlan.endDate);
            const diffTime = expiry - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return Math.max(0, diffDays);
        }
        return 0;
    };

    const getCurrentPlanLevel = () => {
        if (!currentPlan || !plans.length) return -1;
        const currentPlanData = plans.find(p => p.name.trim() === currentPlan.planName.trim());
        return currentPlanData ? currentPlanData.level : -1;
    };

    const determineChangeType = (targetPlan) => {
        const currentLevel = getCurrentPlanLevel();
        if (currentLevel === -1) return null;
        
        if (targetPlan.level > currentLevel) return 'upgrade';
        if (targetPlan.level < currentLevel) return 'downgrade';
        return 'same';
    };

    const handlePlanSelection = (plan) => {
        const changeTypeResult = determineChangeType(plan);
        
        if (changeTypeResult === 'same') {
            message.info('You are already subscribed to this plan');
            return;
        }

        setSelectedPlan(plan);
        setChangeType(changeTypeResult);

        if (isUserOnActivePlan() && (changeTypeResult === 'upgrade' || changeTypeResult === 'downgrade')) {
            setPlanChangeWarningVisible(true);
        } else {
            setSubscribeVisible(true);
        }
    };

    const handlePlanChangeConfirm = (immediate = true) => {
        setPlanChangeWarningVisible(false);
        if (immediate) {
            setSubscribeVisible(true);
        } else {
            // Handle scheduled change logic here if needed
            message.info('Plan change will take effect after your current subscription ends');
            setSelectedPlan(null);
            setChangeType(null);
        }
    };

    const handleSubscribe = async (values) => {
        try {
            setLoading(true);

            await drEasyApi.post('/api/Plans/Subscribe', {
                planId: selectedPlan.id,
                duration: values.duration
            });

            const actionText = changeType === 'upgrade' ? 'upgraded to' : 
                              changeType === 'downgrade' ? 'downgraded to' : 'subscribed to';
            message.success(`Successfully ${actionText} ${selectedPlan.name}!`);
            
            setSubscribeVisible(false);
            subscribeForm.resetFields();
            setSelectedPlan(null);
            setChangeType(null);

            await Promise.all([
                fetchCurrentPlan(),
                fetchSubscriptionHistory()
            ]);
        } catch (error) {
            console.error('Error subscribing:', error);
            message.error(error.response?.data[0] || 'Failed to process subscription');
        }
        setLoading(false);
    };

    const getUniqueFeatures = (plan) => {
        if (!plan || !plan.features) return [];
        
        const sortedPlans = [...plans].sort((a, b) => a.level - b.level);
        
        const lowerLevelFeatures = new Set();
        sortedPlans.forEach(p => {
            if (p.level < plan.level && p.features) {
                p.features.forEach(feature => {
                    lowerLevelFeatures.add(feature.featureKey);
                });
            }
        });
        
        return plan.features.filter(feature => !lowerLevelFeatures.has(feature.featureKey));
    };

    const getAllFeatures = (plan) => {
        if (!plan) return [];
        
        const sortedPlans = [...plans].sort((a, b) => a.level - b.level);
        
        const allFeatures = [];
        const addedFeatureKeys = new Set();
        
        sortedPlans.forEach(p => {
            if (p.level <= plan.level && p.features) {
                p.features.forEach(feature => {
                    if (!addedFeatureKeys.has(feature.featureKey)) {
                        allFeatures.push(feature);
                        addedFeatureKeys.add(feature.featureKey);
                    }
                });
            }
        });
        
        return allFeatures;
    };

    const getInheritedFeatures = (plan) => {
        if (!plan) return [];
        
        const sortedPlans = [...plans].sort((a, b) => a.level - b.level);
        const inheritedFeatures = [];
        const addedFeatureKeys = new Set();
        
        sortedPlans.forEach(p => {
            if (p.level < plan.level && p.features) {
                p.features.forEach(feature => {
                    if (!addedFeatureKeys.has(feature.featureKey)) {
                        inheritedFeatures.push(feature);
                        addedFeatureKeys.add(feature.featureKey);
                    }
                });
            }
        });
        
        return inheritedFeatures;
    };

    const getPlanTheme = (level) => {
        const themes = {
            0: {
                color: '#298E9F',
                gradient: 'linear-gradient(135deg, #298E9F 0%, #1F6F7D 100%)',
                bgGradient: 'linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(64, 169, 255, 0.05) 100%)',
                icon: <RocketOutlined />,
                name: 'Smart Start'
            },
            1: {
                color: '#faad14',
                gradient: 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
                bgGradient: 'linear-gradient(135deg, rgba(250, 173, 20, 0.1) 0%, rgba(255, 197, 61, 0.05) 100%)',
                icon: <CrownOutlined />,
                name: 'Elite Care'
            },
            2: {
                color: '#298E9F',
                gradient: 'linear-gradient(135deg, #298E9F 0%, #1F6F7D 100%)',
                bgGradient: 'linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(64, 169, 255, 0.05) 100%)',
                icon: <StarOutlined />,
                name: 'Professional'
            }
        };
        return themes[level] || themes[0];
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const isCurrentPlan = (planName) => {
        return currentPlan && currentPlan.planName === planName && currentPlan.status === "Active";
    };

    const getStatusColor = (status) => {
        const statusColors = {
            'Active': 'green',
            'Expired': 'red',
            'Cancelled': 'default',
            'Pending': 'orange'
        };
        return statusColors[status] || 'default';
    };

    const getDurationText = (duration) => {
        if (typeof duration === 'string') {
            return duration;
        }
        return duration === 0 ? '1 month' : `${duration} months`;
    };

    const getProgressPercent = () => {
        if (currentPlan && currentPlan.startDate && currentPlan.endDate) {
            const start = new Date(currentPlan.startDate);
            const end = new Date(currentPlan.endDate);
            const now = new Date();
            const total = end - start;
            const elapsed = now - start;
            return Math.min(100, Math.max(0, (elapsed / total) * 100));
        }
        return 0;
    };

    const getButtonText = (plan) => {
        const changeTypeResult = determineChangeType(plan);
        if (changeTypeResult === 'same') {
            return <><CheckOutlined /> Current Plan</>;
        } else if (changeTypeResult === 'upgrade') {
            return <><ArrowUpOutlined /> Upgrade to {plan.name}</>;
        } else if (changeTypeResult === 'downgrade') {
            return <><ArrowDownOutlined /> Downgrade to {plan.name}</>;
        } else {
            return <><ThunderboltOutlined /> Choose {plan.name}</>;
        }
    };

    const getButtonStyle = (plan) => {
        const changeTypeResult = determineChangeType(plan);
        const theme = getPlanTheme(plan.level);
        
        if (changeTypeResult === 'same') {
            return {
                background: '#f5f5f5',
                color: '#999',
                boxShadow: 'none',
            };
        } else if (changeTypeResult === 'upgrade') {
            return {
                background: 'linear-gradient(135deg, #1F6F7D 0%, #298E9F 100%)',
                color: 'white',
                boxShadow: '0 4px 15px rgba(26, 196, 168, 0.3)',
            };
        } else if (changeTypeResult === 'downgrade') {
            return {
                background: 'linear-gradient(135deg, #fa8c16 0%, #ffc53d 100%)',
                color: 'white',
                boxShadow: '0 4px 15px rgba(250, 140, 22, 0.3)',
            };
        } else {
            return {
                background: theme.gradient,
                color: 'white',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
            };
        }
    };
console.log('============subscriptionHistory========================');
console.log(subscriptionHistory);
console.log('====================================');
    return (
        <div style={{ 
            padding: isMobile ? '16px' : '24px', 
            minHeight: '100vh',
        }}>
            <style jsx>{`
                .plan-card {
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    cursor: pointer;
                }
                .plan-card:hover {
                    transform: ${isMobile ? 'none' : 'translateY(-8px)'} !important;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
                }
                .popular-badge {
                    animation: pulse 2s infinite;
                }
                .feature-item {
                    transition: all 0.3s ease;
                }
                .feature-item:hover {
                    background: rgba(24, 144, 255, 0.05);
                    transform: ${isMobile ? 'none' : 'translateX(4px)'};
                }
                .glass-card {
                    backdrop-filter: blur(10px);
                    background: rgba(255, 255, 255, 0.95);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }
                .responsive-tabs .ant-tabs-nav {
                    margin-bottom: ${isMobile ? '16px' : '24px'} !important;
                }
                .responsive-tabs .ant-tabs-tab {
                    padding: ${isMobile ? '8px 12px' : '12px 16px'} !important;
                    font-size: ${isMobile ? '14px' : '16px'} !important;
                    margin: ${isMobile ? '0 4px' : '0 8px'} !important;
                }
                .responsive-tabs .ant-tabs-nav-wrap {
                    justify-content: center;
                }
                .responsive-tabs .ant-tabs-tab-btn {
                    display: flex;
                    align-items: center;
                    gap: ${isMobile ? '4px' : '8px'};
                }
                ${isMobile ? `
                .responsive-tabs .ant-tabs-nav-scroll {
                    padding: 0 16px;
                }
                .responsive-tabs .ant-tabs-nav {
                    justify-content: flex-start;
                }
                .responsive-tabs .ant-tabs-nav-wrap {
                    justify-content: flex-start;
                }
                ` : ''}
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                }
                .glow-button {
                    position: relative;
                    overflow: hidden;
                }
                .glow-button::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                    transition: left 0.5s;
                }
                .glow-button:hover::before {
                    left: 100%;
                }
                @media (max-width: 768px) {
                    .mobile-stack {
                        margin-bottom: 16px !important;
                    }
                    .mobile-full-width {
                        width: 100% !important;
                    }
                    .mobile-text-center {
                        text-align: center !important;
                    }
                }
            `}</style>

            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    size={isMobile ? "default" : "large"}
                    centered={!isMobile}
                    className="responsive-tabs"
                    tabPosition={isMobile ? 'top' : 'top'}
                    style={{
                        '.ant-tabs-tab': {
                            fontSize: isMobile ? '14px' : '16px',
                            fontWeight: '600'
                        }
                    }}
                >
                    <TabPane
                        tab={
                            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                <StarOutlined />
                                {!isMobile && ' Available Plans'}
                                {isMobile && ' Plans'}
                            </span>
                        }
                        key="1"
                    >
                        {/* Enhanced Billing Toggle */}
                        <div style={{ 
                            textAlign: 'center', 
                            marginBottom: isMobile ? '32px' : '48px',
                            padding: isMobile ? '16px' : '24px',
                        }}>
                            <Button.Group size={isMobile ? 'middle' : 'large'}>
                                <Button
                                    type={billingType === 'monthly' ? 'primary' : 'default'}
                                    onClick={() => setBillingType('monthly')}
                                    style={{
                                        height: isMobile ? '40px' : '48px',
                                        paddingLeft: isMobile ? '16px' : '24px',
                                        paddingRight: isMobile ? '16px' : '24px',
                                        fontSize: isMobile ? '14px' : '16px',
                                        fontWeight: '600'
                                    }}
                                >
                                    Monthly
                                </Button>
                                <Button
                                    type={billingType === 'yearly' ? 'primary' : 'default'}
                                    onClick={() => setBillingType('yearly')}
                                    style={{
                                        height: isMobile ? '40px' : '48px',
                                        paddingLeft: isMobile ? '16px' : '24px',
                                        paddingRight: isMobile ? '16px' : '24px',
                                        fontSize: isMobile ? '14px' : '16px',
                                        fontWeight: '600'
                                    }}
                                >
                                    Yearly
                                    <Badge
                                        count="Save 20%"
                                        style={{ 
                                            backgroundColor: '#52c41a', 
                                            marginLeft: '8px',
                                            animation: 'pulse 2s infinite',
                                            fontSize: isMobile ? '10px' : '12px'
                                        }}
                                    />
                                </Button>
                            </Button.Group>
                        </div>

                        <Spin spinning={plansLoading}>
                            <Row gutter={[isMobile ? 16 : 32, isMobile ? 16 : 32]} justify="center">
                                {[...plans].sort((a, b) => a.level - b.level).map((plan) => {
                                    const uniqueFeatures = getUniqueFeatures(plan);
                                    const inheritedFeatures = getInheritedFeatures(plan);
                                    const theme = getPlanTheme(plan.level);
                                    const isPopular = plan.level === 1;
                                    const isCurrent = isCurrentPlan(plan.name);
                                    const changeTypeResult = determineChangeType(plan);
                                    
                                    return (
                                        <Col xs={24} sm={24} md={8} key={plan.id} className="mobile-stack">
                                            <Card 
                                                className="plan-card glass-card"
                                                style={{
                                                    height: plan.level==1?"100%":"90%",
                                                    marginTop: plan.level==1?"":"15%",
                                                    position: 'relative',
                                                    border: isCurrent ? `3px solid ${theme.color}` : 
                                                           isPopular ? `2px solid ${theme.color}` : '1px solid #f0f0f0',
                                                    transform: (isPopular && !isMobile) ? 'scale(1.05)' : 'scale(1)',
                                                    background: theme.bgGradient,
                                                    borderRadius: '20px',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {/* Current Plan Badge */}
                                                {isCurrent && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '0',
                                                        left: '0',
                                                        right: '0',
                                                        background: theme.gradient,
                                                        color: 'white',
                                                        padding: isMobile ? '6px' : '8px',
                                                        textAlign: 'center',
                                                        fontSize: isMobile ? '12px' : '14px',
                                                        fontWeight: 'bold',
                                                        zIndex: 2
                                                    }}>
                                                        <CheckOutlined /> CURRENT PLAN
                                                    </div>
                                                )}

                                                {/* Popular Badge */}
                                                {isPopular && !isCurrent && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '0',
                                                        left: '0',
                                                        right: '0',
                                                        background: theme.gradient,
                                                        color: 'white',
                                                        padding: isMobile ? '6px' : '8px',
                                                        textAlign: 'center',
                                                        fontSize: isMobile ? '12px' : '14px',
                                                        fontWeight: 'bold',
                                                        zIndex: 2
                                                    }}>
                                                        <FireOutlined className="popular-badge" /> MOST POPULAR
                                                    </div>
                                                )}

                                         

                                                <div style={{ 
                                                    paddingTop: (isCurrent || isPopular) ? (isMobile ? '16px' : '34px') : '0',
                                                    textAlign: 'center', 
                                                    marginBottom: isMobile ? '4px' : '3px',
                                                }}>
                                                    {/* Plan Icon */}
                                                    <div style={{
                                                        width: isMobile ? '60px' : '80px',
                                                        height: isMobile ? '60px' : '80px',
                                                        borderRadius: '50%',
                                                        background: theme.gradient,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        margin: `0 auto ${isMobile ? '16px' : '24px'}`,
                                                        fontSize: isMobile ? '24px' : '32px',
                                                        color: 'white',
                                                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)'
                                                    }}>
                                                        {theme.icon}
                                                    </div>

                                                    {/* Plan Name */}
                                                    <Title level={isMobile ? 4 : 3} style={{ 
                                                        marginBottom: '8px',
                                                        fontSize: isMobile ? '20px' : '24px',
                                                        fontWeight: '700',
                                                        color: theme.color
                                                    }}>
                                                        {plan.name}
                                                    </Title>

                                                    {/* Plan Description */}
                                                    <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                                                        {plan.level === 0 ? 'Perfect for getting started' :
                                                         plan.level === 1 ? 'Most popular choice' :
                                                         'Complete solution for power users'}
                                                    </Text>

                                                    {/* Pricing */}
                                                    <div style={{ margin: isMobile ? '16px 0' : '24px 0' }}>
                                                        <div style={{ position: 'relative' }}>
                                                            <Title level={isMobile ? 2 : 1} style={{
                                                                color: theme.color,
                                                                marginBottom: '4px',
                                                                fontSize: isMobile ? '36px' : '48px',
                                                                fontWeight: '800',
                                                                lineHeight: '1'
                                                            }}>
                                                                 {billingType === 'monthly' ? (plan.monthlyPrice).toLocaleString('en-US', { maximumFractionDigits: 2 }) : plan.annualPrice} EGP
                                                            </Title>
                                                            <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                                                per {billingType === 'monthly' ? 'month' : 'year'}
                                                            </Text>
                                                        </div>
                                                        
                                                        {billingType === 'yearly' && (
                                                            <div style={{ marginTop: '12px' }}>
                                                                <Text type="secondary" delete style={{ fontSize: isMobile ? '12px' : '14px' }}>
                                                                    EGP {(plan.monthlyPrice * 12).toFixed(2)} per year
                                                                </Text>
                                                                <br />
                                                                <Tag color="success" style={{ 
                                                                    fontSize: isMobile ? '10px' : '12px',
                                                                    fontWeight: 'bold',
                                                                    marginTop: '4px'
                                                                }}>
                                                                    Save EGP{((plan.monthlyPrice * 12) - plan.annualPrice).toFixed(2)}
                                                                </Tag>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <Divider style={{ margin: '0 0 24px 0', borderColor: theme.color, opacity: 0.2 }} />

                                                {/* Features Section */}
                                                <div style={{ 
                                                    marginBottom: isMobile ? '24px' : '32px', 
                                                    minHeight: isMobile ? 'auto' : '200px',
                                                    padding: isMobile ? '0 16px' : '0 24px'
                                                }}>
                                                    {plan.level > 0 && (
                                                        <List.Item className="feature-item" style={{ 
                                                            padding: isMobile ? '8px 12px' : '12px 16px', 
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            marginBottom: '4px'
                                                        }}>
                                                            <Space>
                                                                <div style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    borderRadius: '50%',
                                                                    background: theme.color,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '10px',
                                                                    color: 'white'
                                                                }}>
                                                                    <CheckOutlined />
                                                                </div>
                                                                <Text style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '500' }}>
                                                                    Everything in {plan.level == 1 ? "Smart Start" : "Pro Clinic"}
                                                                </Text>
                                                            </Space>
                                                        </List.Item>
                                                    )}
                                                    
                                                    <List
                                                        dataSource={uniqueFeatures.length > 0 ? uniqueFeatures : (plan.features || [])}
                                                        renderItem={(feature) => (
                                                            <List.Item className="feature-item" style={{ 
                                                                padding: isMobile ? '8px 12px' : '12px 16px', 
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                marginBottom: '4px'
                                                            }}>
                                                                <Space>
                                                                    <div style={{
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        borderRadius: '50%',
                                                                        background: theme.color,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '10px',
                                                                        color: 'white'
                                                                    }}>
                                                                        <CheckOutlined />
                                                                    </div>
                                                                    <Text style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '500' }}>
                                                                        {feature.featureName || feature.name}
                                                                    </Text>
                                                                </Space>
                                                            </List.Item>
                                                        )}
                                                    />
                                                </div>

                                                {/* CTA Button */}
                                                <div style={{ padding: isMobile ? '0 16px 16px' : '0 24px 24px' }}>
                                                    <Button 
                                                        className="glow-button"
                                                        size={isMobile ? 'middle' : 'large'} 
                                                        block
                                                        disabled={changeTypeResult === 'same'}
                                                        onClick={() => handlePlanSelection(plan)}
                                                        style={{
                                                            height: isMobile ? '44px' : '56px',
                                                            fontSize: isMobile ? '14px' : '16px',
                                                            fontWeight: 'bold',
                                                            borderRadius: '12px',
                                                            border: 'none',
                                                            marginTop: 'auto',
                                                            ...getButtonStyle(plan)
                                                        }}
                                                    >
                                                        {getButtonText(plan)}
                                                    </Button>
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </Spin>
                    </TabPane>

                    <TabPane
                        tab={
                            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                <CalendarOutlined />
                                {!isMobile && ' My Subscription'}
                                {isMobile && ' Current'}
                            </span>
                        }
                        key="2"
                    >
                        <Row gutter={[isMobile ? 16 : 24, isMobile ? 16 : 24]}>
                            <Col xs={24} lg={isMobile ? 24 : 24}>
                                <Card  
                                    title={
                                        <Title level={isMobile ? 5 : 4} style={{ margin: 0, color: '#298E9F' }}>
                                            <TrophyOutlined /> Current Subscription
                                        </Title>
                                    }
                                    className="glass-card px-10"
                                    style={{ 
                                        marginBottom: isMobile ? '16px' : '24px',
                                        borderRadius: '16px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Spin spinning={currentPlanLoading}>
                                        {currentPlan ? (
                                            <div>
                                                {/* Progress Bar */}
                                                <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between', 
                                                        alignItems: 'center',
                                                        marginBottom: '8px',
                                                        flexDirection: isMobile ? 'column' : 'row',
                                                        gap: isMobile ? '4px' : '0'
                                                    }}>
                                                        <Text style={{ fontWeight: '600', fontSize: isMobile ? '14px' : '16px' }}>
                                                            Subscription Progress
                                                        </Text>
                                                        <Text style={{ 
                                                            color: '#298E9F', 
                                                            fontWeight: '600',
                                                            fontSize: isMobile ? '14px' : '16px'
                                                        }}>
                                                            {getDaysRemaining()} days remaining
                                                        </Text>
                                                    </div>
                                                    <Progress 
                                                        percent={getProgressPercent().toFixed(2)} 
                                                        strokeColor={{
                                                            '0%': '#298E9F',
                                                            '100%': '#298E9F',
                                                        }}
                                                        trailColor="#f0f0f0"
                                                        strokeWidth={isMobile ? 6 : 8}
                                                        style={{ marginBottom: '16px' }}
                                                    />
                                                </div>

                                                <Row gutter={[16, 16]}>
                                                    <Col xs={24} sm={isMobile ? 24 : 12}>
                                                        <div style={{
                                                            padding: isMobile ? '16px' : '20px',
                                                            background: 'linear-gradient(135deg, #298E9F 0%, #298E9F 100%)',
                                                            borderRadius: '12px',
                                                            color: 'white',
                                                            textAlign: 'center'
                                                        }}>
                                                            <div style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: '8px' }}>
                                                                {getPlanTheme(getCurrentPlanLevel()).icon}
                                                            </div>
                                                            <Title level={isMobile ? 5 : 4} style={{ color: 'white', margin: 0 }}>
                                                                {currentPlan.planName}
                                                            </Title>
                                                            
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} sm={isMobile ? 24 : 12}>
                                                        <Row gutter={[16, 16]}>
                                                            <Col span={24}>
                                                                <Statistic
                                                                    title="Monthly Price"
                                                                    value={parseInt(currentPlan.monthlyPrice).toFixed(2)}
                                                                    prefix={<DollarOutlined />}
                                                                    precision={2}
                                                                    valueStyle={{ 
                                                                        color: '#298E9F', 
                                                                        fontWeight: 'bold',
                                                                        fontSize: isMobile ? '20px' : '24px'
                                                                    }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                    <Col xs={24} sm={isMobile ? 24 : 12}>
                                                        <Statistic
                                                            title="Subscription Date"
                                                            value={formatDate(currentPlan.startDate)}
                                                            prefix={<CalendarOutlined />}
                                                            valueStyle={{ fontSize: isMobile ? '14px' : '16px' }}
                                                        />
                                                    </Col>
                                                    <Col xs={24} sm={isMobile ? 24 : 12}>
                                                        <Statistic
                                                            title="Expires On"
                                                            value={formatDate(currentPlan.endDate)}
                                                            prefix={<ClockCircleOutlined />}
                                                            valueStyle={{ 
                                                                fontSize: isMobile ? '14px' : '16px',
                                                                color: getDaysRemaining() < 7 ? '#ff4d4f' : '#298E9F'
                                                            }}
                                                        />
                                                    </Col>
                                                </Row>

                                                <Divider />

                                                <div style={{ 
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: isMobile ? '8px' : '16px',
                                                    justifyContent: isMobile ? 'center' : 'flex-start'
                                                }}>
                                                    <Tag 
                                                        color={currentPlan.status === "Active" ? 'green' : 'red'} 
                                                        icon={<CheckOutlined />}
                                                        style={{ 
                                                            padding: isMobile ? '4px 12px' : '6px 16px',
                                                            fontSize: isMobile ? '12px' : '14px',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {currentPlan.status === "Active" ? "Active" : 'INACTIVE'}
                                                    </Tag>
                                              
                                                    {getDaysRemaining() < 7 && getDaysRemaining() > 0 && (
                                                        <Tag 
                                                            color="volcano"
                                                            style={{ 
                                                                padding: isMobile ? '4px 12px' : '6px 16px',
                                                                fontSize: isMobile ? '12px' : '14px',
                                                                fontWeight: 'bold',
                                                                animation: 'pulse 2s infinite'
                                                            }}
                                                        >
                                                            <ClockCircleOutlined /> Expiring Soon
                                                        </Tag>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: isMobile ? '32px 16px' : '40px 20px' }}>
                                                <div style={{
                                                    fontSize: isMobile ? '48px' : '64px',
                                                    color: '#d9d9d9',
                                                    marginBottom: '16px'
                                                }}>
                                                    <GiftOutlined />
                                                </div>
                                                <Title level={isMobile ? 5 : 4} type="secondary">
                                                    No Active Subscription
                                                </Title>
                                                <Paragraph type="secondary" style={{ 
                                                    fontSize: isMobile ? '14px' : '16px', 
                                                    marginBottom: isMobile ? '16px' : '24px' 
                                                }}>
                                                    Choose a plan to unlock premium features and get started!
                                                </Paragraph>
                                                <Button 
                                                    type="primary" 
                                                    size={isMobile ? 'middle' : 'large'}
                                                    onClick={() => setActiveTab('1')}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #298E9F 0%, #298E9F 100%)',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        height: isMobile ? '40px' : '48px',
                                                        paddingLeft: isMobile ? '24px' : '32px',
                                                        paddingRight: isMobile ? '24px' : '32px',
                                                        fontSize: isMobile ? '14px' : '16px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    <RocketOutlined /> Browse Plans
                                                </Button>
                                            </div>
                                        )}
                                    </Spin>
                                </Card>
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane
                        tab={
                            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                <HistoryOutlined />
                                {!isMobile && ' Subscription History'}
                                {isMobile && ' History'}
                            </span>
                        }
                        key="3"
                    >
                        <Card 
                            title={
                                <Title level={isMobile ? 5 : 4} style={{ margin: 0, color: '#298E9F' }}>
                                    <HistoryOutlined /> Subscription History
                                </Title>
                            }
                            className="glass-card"
                            style={{ borderRadius: '16px' }}
                        >
                            <Spin spinning={historyLoading}>
                                {subscriptionHistory.length > 0 ? (
                                    <Timeline mode={isMobile ? 'left' : 'left'}>
                                        {subscriptionHistory.map((subscription, index) => (
                                            <Timeline.Item
                                                key={subscription.id || index}
                                                color={getStatusColor(subscription.status)}
                                                dot={
                                                    <div style={{
                                                        width: isMobile ? '24px' : '32px',
                                                        height: isMobile ? '24px' : '32px',
                                                        borderRadius: '50%',
                                                        background: subscription.status === 'Active' ? '#298E9F' : '#d9d9d9',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        fontSize: isMobile ? '12px' : '14px'
                                                    }}>
                                                        {subscription.status === 'Active' ? 
                                                            <CheckOutlined /> : <ClockCircleOutlined />
                                                        }
                                                    </div>
                                                }
                                            >
                                                <Card 
                                                    size="small" 
                                                    style={{ 
                                                        marginBottom: '16px',
                                                        borderRadius: '12px',
                                                        border: '1px solid #f0f0f0',
                                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                                                    }}
                                                >
                                                    <Row gutter={[16, 8]}>
                                                        <Col xs={24} sm={isMobile ? 24 : 12} md={isMobile ? 24 : 12}>
                                                            <div style={{ marginBottom: '8px' }}>
                                                                <Text strong style={{ 
                                                                    fontSize: isMobile ? '14px' : '16px', 
                                                                    color: '#298E9F' 
                                                                }}>
                                                                    {subscription.planName}
                                                                </Text>
                                                                {subscription.isFreeTrial && (
                                                                    <Tag 
                                                                        color="green" 
                                                                        size="small" 
                                                                        style={{ 
                                                                            marginLeft: '8px',
                                                                            fontSize: '10px'
                                                                        }}
                                                                    >
                                                                        <GiftOutlined /> Free Trial
                                                                    </Tag>
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs={24} sm={isMobile ? 24 : 12} md={isMobile ? 24 : 6}>
                                                            <div className={isMobile ? 'mobile-text-center' : ''}>
                                                                <Text style={{ 
                                                                    fontSize: isMobile ? '16px' : '18px', 
                                                                    fontWeight: 'bold',
                                                                    color: '#298E9F'
                                                                }}>
                                                                    EGP {subscription.price || 0}
                                                                </Text>
                                                                <br />
                                                                <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                                    {getDurationText(subscription.duration)}
                                                                </Text>
                                                            </div>
                                                        </Col>
                                                        <Col xs={24} sm={isMobile ? 24 : 12} md={isMobile ? 24 : 6}>
                                                            <div className={isMobile ? 'mobile-text-center' : ''}>
                                                                <div style={{ marginBottom: '4px' }}>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>Start:</Text>
                                                                    <br />
                                                                    <Text style={{ 
                                                                        fontSize: isMobile ? '12px' : '14px', 
                                                                        fontWeight: '500' 
                                                                    }}>
                                                                        {formatDate(subscription.subscriptionDate)}
                                                                    </Text>
                                                                </div>
                                                                <div>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>End:</Text>
                                                                    <br />
                                                                    <Text style={{ 
                                                                        fontSize: isMobile ? '12px' : '14px', 
                                                                        fontWeight: '500' 
                                                                    }}>
                                                                        {formatDate(subscription.endDate)}
                                                                    </Text>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        {/* <Col xs={24} sm={isMobile ? 24 : 12} md={isMobile ? 24 : 6}>
                                                            <div className={isMobile ? 'mobile-text-center' : ''}>
                                                                <Tag 
                                                                    color={getStatusColor(subscription.status)}
                                                                    style={{
                                                                        padding: '4px 12px',
                                                                        fontSize: isMobile ? '12px' : '13px',
                                                                        fontWeight: 'bold',
                                                                        borderRadius: '16px'
                                                                    }}
                                                                >
                                                                    {subscription.status}
                                                                </Tag>
                                                            </div>
                                                        </Col> */}
                                                    </Row>
                                                </Card>
                                            </Timeline.Item>
                                        ))}
                                    </Timeline>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: isMobile ? '48px 16px' : '60px 20px' }}>
                                        <div style={{
                                            fontSize: isMobile ? '48px' : '64px',
                                            color: '#d9d9d9',
                                            marginBottom: '16px'
                                        }}>
                                            <HistoryOutlined />
                                        </div>
                                        <Title level={isMobile ? 5 : 4} type="secondary">
                                            No Subscription History
                                        </Title>
                                        <Paragraph type="secondary" style={{ 
                                            fontSize: isMobile ? '14px' : '16px', 
                                            marginBottom: isMobile ? '16px' : '24px' 
                                        }}>
                                            Your subscription history will appear here once you subscribe to a plan.
                                        </Paragraph>
                                        <Button 
                                            type="primary" 
                                            size={isMobile ? 'middle' : 'large'}
                                            onClick={() => setActiveTab('1')}
                                            style={{
                                                background: 'linear-gradient(135deg, #298E9F 0%, #298E9F 100%)',
                                                border: 'none',
                                                borderRadius: '8px',
                                                height: isMobile ? '40px' : '48px',
                                                paddingLeft: isMobile ? '24px' : '32px',
                                                paddingRight: isMobile ? '24px' : '32px',
                                                fontSize: isMobile ? '14px' : '16px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            <RocketOutlined /> Get Started
                                        </Button>
                                    </div>
                                )}
                            </Spin>
                        </Card>
                    </TabPane>
                </Tabs>

                {/* Plan Change Warning Modal */}
                <Modal
                    title={
                        <div style={{ textAlign: 'center' }}>
                            <WarningOutlined style={{ color: '#faad14', fontSize: '24px', marginRight: '8px' }} />
                            <span>Plan Change Confirmation</span>
                        </div>
                    }
                    open={planChangeWarningVisible}
                    onCancel={() => {
                        setPlanChangeWarningVisible(false);
                        setSelectedPlan(null);
                        setChangeType(null);
                    }}
                    footer={null}
                    width={isMobile ? '95%' : 500}
                    centered
                >
                    {selectedPlan && (
                        <div style={{ textAlign: 'center', padding: isMobile ? '16px 0' : '24px 0' }}>
                            <Alert
                                message={`You still have ${getDaysRemaining()} days left in your current plan`}
                                description={
                                    <div style={{ marginTop: '16px' }}>
                                        <Text>
                                            You want to {changeType} from <strong>{currentPlan?.planName}</strong> to <strong>{selectedPlan.name}</strong>.
                                        </Text>
                                        <br />
                                        <br />
                                        <Text>
                                            Do you want to switch immediately or wait until your current plan ends?
                                        </Text>
                                    </div>
                                }
                                type="warning"
                                showIcon
                                style={{ 
                                    marginBottom: '24px',
                                    textAlign: 'left'
                                }}
                            />
                            
                            <Space direction={isMobile ? 'vertical' : 'horizontal'} size="large" style={{ width: '100%' }}>
                                <Button
                                    size="large"
                                    onClick={() => handlePlanChangeConfirm(true)}
                                    style={{
                                        background: changeType === 'upgrade' ? 
                                            'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)' :
                                            'linear-gradient(135deg, #fa8c16 0%, #ffc53d 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 'bold',
                                        height: '48px',
                                        paddingLeft: '24px',
                                        paddingRight: '24px',
                                        width: isMobile ? '100%' : 'auto'
                                    }}
                                >
                                    <ThunderboltOutlined /> Switch Immediately
                                </Button>
                                
                           
                                
                                <Button
                                    size="large"
                                    onClick={() => {
                                        setPlanChangeWarningVisible(false);
                                        setSelectedPlan(null);
                                        setChangeType(null);
                                    }}
                                    style={{
                                        borderRadius: '8px',
                                        height: '48px',
                                        paddingLeft: '24px',
                                        paddingRight: '24px',
                                        width: isMobile ? '100%' : 'auto'
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Space>
                        </div>
                    )}
                </Modal>

                {/* Enhanced Subscribe Modal */}
                <Modal
                    title={null}
                    open={subscribeVisible}
                    onCancel={() => {
                        setSubscribeVisible(false);
                        subscribeForm.resetFields();
                        setSelectedPlan(null);
                        setChangeType(null);
                    }}
                    footer={null}
                    width={isMobile ? '95%' : 600}
                    style={{
                        top: isMobile ? 10 : 20,
                    }}
                    bodyStyle={{
                        padding: 0,
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }}
                >
                    {selectedPlan && (
                        <div>
                            {/* Modal Header */}
                            <div style={{
                                background: getPlanTheme(selectedPlan.level).gradient,
                                color: 'white',
                                padding: isMobile ? '24px' : '32px',
                                textAlign: 'center',
                                position: 'relative'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="20" cy="20" r="2"/%3E%3C/g%3E%3C/svg%3E")',
                                    opacity: 0.3
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <div style={{ 
                                        fontSize: isMobile ? '36px' : '48px', 
                                        marginBottom: '16px' 
                                    }}>
                                        {getPlanTheme(selectedPlan.level).icon}
                                    </div>
                                    <Title level={isMobile ? 3 : 2} style={{ color: 'white', margin: 0 }}>
                                        {changeType === 'upgrade' ? `Upgrade to ${selectedPlan.name}` :
                                         changeType === 'downgrade' ? `Downgrade to ${selectedPlan.name}` :
                                         `Subscribe to ${selectedPlan.name}`}
                                    </Title>
                                    <Tag 
                                        style={{ 
                                            marginTop: '8px',
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            color: 'white',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            fontSize: isMobile ? '12px' : '14px'
                                        }}
                                    >
                                        Level {selectedPlan.level}
                                    </Tag>
                                    {changeType && (
                                        <Tag 
                                            style={{ 
                                                marginTop: '8px',
                                                marginLeft: '8px',
                                                background: changeType === 'upgrade' ? 
                                                    'rgba(82, 196, 26, 0.2)' : 'rgba(250, 140, 22, 0.2)',
                                                color: 'white',
                                                border: changeType === 'upgrade' ? 
                                                    '1px solid rgba(82, 196, 26, 0.3)' : '1px solid rgba(250, 140, 22, 0.3)',
                                                fontSize: isMobile ? '12px' : '14px'
                                            }}
                                        >
                                            {changeType === 'upgrade' ? 
                                                <><ArrowUpOutlined /> UPGRADE</> : 
                                                <><ArrowDownOutlined /> DOWNGRADE</>
                                            }
                                        </Tag>
                                    )}
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div style={{ padding: isMobile ? '24px' : '32px' }}>
                                <Form
                                    form={subscribeForm}
                                    layout="vertical"
                                    onFinish={handleSubscribe}
                                    initialValues={{ duration: 1 }}
                                >
                                    <Form.Item
                                        name="duration"
                                        label={
                                            <Text style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '600' }}>
                                                Choose Your Subscription Duration
                                            </Text>
                                        }
                                        rules={[{ required: true, message: 'Please select duration' }]}
                                    >
                                        <Select 
                                            placeholder="Select duration" 
                                            size={isMobile ? 'middle' : 'large'}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            <Select.Option value={1}>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    padding: '8px 0'
                                                }}>
                                                    <span>
                                                        <CalendarOutlined style={{ marginRight: '8px', color: '#298E9F' }} />
                                                        1 Month
                                                    </span>
                                                    <Text strong style={{ color: '#298E9F' }}>
                                                        EGP {selectedPlan.monthlyPrice}
                                                    </Text>
                                                </div>
                                            </Select.Option>
                                            <Select.Option value={12}>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    padding: '8px 0'
                                                }}>
                                                    <span>
                                                        <CalendarOutlined style={{ marginRight: '8px', color: '#298E9F' }} />
                                                        12 Months
                                                        <Tag 
                                                            color="success" 
                                                            size="small" 
                                                            style={{ marginLeft: '8px' }}
                                                        >
                                                            Save EGP {((selectedPlan.monthlyPrice * 12) - selectedPlan.annualPrice).toFixed(2)}
                                                        </Tag>
                                                    </span>
                                                    <Text strong style={{ color: '#298E9F' }}>
                                                        EGP {selectedPlan.annualPrice}
                                                    </Text>
                                                </div>
                                            </Select.Option>
                                        </Select>
                                    </Form.Item>

                                    {/* Features Preview */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, rgba(41, 142, 159, 0.05) 0%, rgba(82, 196, 26, 0.05) 100%)',
                                        borderRadius: '12px',
                                        padding: isMobile ? '16px' : '20px',
                                        marginBottom: isMobile ? '16px' : '24px',
                                        border: '1px solid rgba(41, 142, 159, 0.1)'
                                    }}>
                                        <Title level={5} style={{ 
                                            marginBottom: '16px',
                                            color: '#298E9F',
                                            fontSize: isMobile ? '14px' : '16px'
                                        }}>
                                            <GiftOutlined /> What's Included
                                        </Title>
                                        <List 
                                            size="small"
                                            dataSource={getAllFeatures(selectedPlan)}
                                            renderItem={(feature, index) => (
                                                <List.Item 
                                                    style={{ 
                                                        padding: '6px 0', 
                                                        border: 'none'
                                                    }}
                                                >
                                                    <Space>
                                                        <div style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '50%',
                                                            background: '#298E9F',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '10px',
                                                            color: 'white'
                                                        }}>
                                                            <CheckOutlined />
                                                        </div>
                                                        <Text style={{ fontSize: isMobile ? '12px' : '14px' }}>
                                                            {feature.featureName || feature.name}
                                                        </Text>
                                                    </Space>
                                                </List.Item>
                                            )}
                                        />
                                    </div>

                                    {/* Action Buttons */}
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <div style={{ 
                                            display: 'flex',
                                            gap: isMobile ? '12px' : '16px',
                                            flexDirection: isMobile ? 'column' : 'row',
                                            justifyContent: isMobile ? 'stretch' : 'space-between'
                                        }}>
                                            <Button 
                                                size={isMobile ? 'middle' : 'large'}
                                                onClick={() => {
                                                    setSubscribeVisible(false);
                                                    subscribeForm.resetFields();
                                                    setSelectedPlan(null);
                                                    setChangeType(null);
                                                }}
                                                style={{
                                                    borderRadius: '8px',
                                                    height: isMobile ? '44px' : '48px',
                                                    paddingLeft: '24px',
                                                    paddingRight: '24px',
                                                    flex: isMobile ? '1' : 'none'
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button 
                                                type="primary" 
                                                htmlType="submit" 
                                                loading={loading} 
                                                size={isMobile ? 'middle' : 'large'}
                                                className="glow-button"
                                                style={{
                                                    background: getPlanTheme(selectedPlan.level).gradient,
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    height: isMobile ? '44px' : '48px',
                                                    paddingLeft: isMobile ? '24px' : '32px',
                                                    paddingRight: isMobile ? '24px' : '32px',
                                                    fontSize: isMobile ? '14px' : '16px',
                                                    fontWeight: 'bold',
                                                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                                                    flex: isMobile ? '1' : 'none'
                                                }}
                                            >
                                                <ThunderboltOutlined /> 
                                                {changeType === 'upgrade' ? 'Upgrade Now' :
                                                 changeType === 'downgrade' ? 'Downgrade Now' :
                                                 'Subscribe Now'}
                                            </Button>
                                        </div>
                                    </Form.Item>
                                </Form>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </div>
    );
};